import React, { useEffect, useRef, useState } from 'react';
import { Download, Upload, Camera, RotateCcw, Trash2, Database, AlertTriangle, ShieldCheck, FileJson, Clock, Recycle, ArchiveRestore } from 'lucide-react';
import { api } from '../../lib/api';
import { toast } from 'sonner';

function formatBytes(n) {
  if (!n) return '0 B';
  const u = ['B', 'KB', 'MB', 'GB'];
  let i = 0; while (n >= 1024 && i < u.length - 1) { n /= 1024; i++; }
  return `${n.toFixed(n >= 10 ? 0 : 1)} ${u[i]}`;
}

function formatDate(iso) {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString('tr-TR', { dateStyle: 'medium', timeStyle: 'short' }); } catch { return iso; }
}

/** Human-friendly countdown until expires_at. Returns 'süresi doldu' if past. */
function formatUntil(iso) {
  if (!iso) return '';
  const ms = new Date(iso).getTime() - Date.now();
  if (ms <= 0) return 'süresi doldu';
  const h = Math.floor(ms / 3600000);
  const m = Math.floor((ms % 3600000) / 60000);
  if (h >= 24) return `${Math.floor(h / 24)} gün ${h % 24} saat`;
  if (h >= 1) return `${h} saat ${m} dk`;
  return `${m} dk`;
}

export default function AdminBackup() {
  const [snapshots, setSnapshots] = useState([]);
  const [trash, setTrash] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [confirmRestore, setConfirmRestore] = useState(null); // { name, fileBlob, mode, keep_admin_users }
  const [restoreUploads, setRestoreUploads] = useState(true);
  const [showTrash, setShowTrash] = useState(false);
  const fileRef = useRef(null);
  const uploadedFileRef = useRef(null); // holds selected .zip File when restoring from disk

  const loadSnapshots = async () => {
    setLoading(true);
    try {
      const [list, trashList] = await Promise.all([
        api.adminBackupList(),
        api.adminBackupTrashList().catch(() => []),
      ]);
      setSnapshots(list || []);
      setTrash(trashList || []);
    } catch (e) {
      toast.error('Yedek listesi alınamadı');
    } finally { setLoading(false); }
  };

  useEffect(() => { loadSnapshots(); }, []);

  const downloadJSON = async () => {
    setBusy(true);
    try {
      const data = await api.adminBackupExport(false);
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const ts = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
      const a = document.createElement('a');
      a.href = url; a.download = `yekuv-backup-${ts}.json`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Yedek dosyası indirildi');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Yedek alınamadı');
    } finally { setBusy(false); }
  };

  const takeSnapshot = async () => {
    setBusy(true);
    try {
      const res = await api.adminBackupSnapshot();
      const cnt = res.upload_count ?? 0;
      const mb = ((res.size || 0) / (1024 * 1024)).toFixed(1);
      toast.success(`Tam yedek oluşturuldu: ${res.name} (${mb} MB · ${cnt} dosya)`);
      loadSnapshots();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Snapshot oluşturulamadı');
    } finally { setBusy(false); }
  };

  const removeSnapshot = async (name) => {
    if (!window.confirm(`"${name}" yedeği Çöp Kutusu'na taşınsın mı? 48 saat içinde geri alabilirsin, sonrasında otomatik silinir.`)) return;
    try {
      const r = await api.adminBackupDelete(name);
      const hours = r?.retention_hours || 48;
      toast.success(`Çöp Kutusu'na taşındı — ${hours} saat içinde geri alabilirsin`);
      loadSnapshots();
    } catch (e) {
      toast.error('Silinemedi');
    }
  };

  const restoreFromTrash = async (trashName, originalName) => {
    if (!window.confirm(`"${originalName}" yedeği Çöp Kutusu'ndan geri alınsın mı?`)) return;
    try {
      const r = await api.adminBackupTrashRestore(trashName);
      toast.success(`Geri alındı: ${r?.restored_as || originalName}`);
      loadSnapshots();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Geri alınamadı');
    }
  };

  const purgeTrashOne = async (trashName, originalName) => {
    if (!window.confirm(`"${originalName}" KALICI olarak silinsin mi? Bu işlem geri alınamaz.`)) return;
    try {
      await api.adminBackupTrashPurge(trashName);
      toast.success('Kalıcı olarak silindi');
      loadSnapshots();
    } catch (e) {
      toast.error('Silinemedi');
    }
  };

  const emptyTrashAll = async () => {
    if (trash.length === 0) return;
    if (!window.confirm(`Çöp Kutusu'ndaki ${trash.length} yedek KALICI olarak silinsin mi? Bu işlem geri alınamaz.`)) return;
    try {
      const r = await api.adminBackupTrashEmpty();
      toast.success(`Çöp Kutusu boşaltıldı (${r?.removed || 0} dosya)`);
      loadSnapshots();
    } catch (e) {
      toast.error('Boşaltılamadı');
    }
  };

  const restoreFromSnapshot = (name) => setConfirmRestore({ snapshot_name: name, source: name, mode: 'replace', keep_admin_users: true });

  const onPickFile = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = '';
    if (!f) return;
    const lower = f.name.toLowerCase();
    if (!lower.endsWith('.zip') && !lower.endsWith('.json')) {
      toast.error('Sadece .zip veya .json yedek dosyaları kabul edilir');
      return;
    }
    if (lower.endsWith('.zip')) {
      // ZIP: use multipart upload endpoint. Store file, open confirm modal.
      uploadedFileRef.current = f;
      setConfirmRestore({ zip_file: f, source: f.name, mode: 'replace', keep_admin_users: true });
      return;
    }
    // JSON legacy
    try {
      const text = await f.text();
      const payload = JSON.parse(text);
      if (!payload?.collections) throw new Error('invalid');
      setConfirmRestore({ payload, source: f.name, mode: 'replace', keep_admin_users: true });
    } catch {
      toast.error('Geçersiz yedek dosyası');
    }
  };

  const doRestore = async () => {
    if (!confirmRestore) return;
    setBusy(true);
    try {
      let res;
      if (confirmRestore.zip_file) {
        res = await api.adminBackupRestoreUpload(confirmRestore.zip_file, {
          mode: confirmRestore.mode || 'replace',
          keep_admin_users: !!confirmRestore.keep_admin_users,
          restore_uploads: restoreUploads,
        });
      } else {
        const body = {
          mode: confirmRestore.mode || 'replace',
          keep_admin_users: !!confirmRestore.keep_admin_users,
          restore_uploads: restoreUploads,
        };
        if (confirmRestore.payload) body.payload = confirmRestore.payload;
        if (confirmRestore.snapshot_name) body.snapshot_name = confirmRestore.snapshot_name;
        res = await api.adminBackupRestore(body);
      }
      const cols = Object.keys(res?.summary || {}).length;
      const upl = res?.uploads?.files_restored || 0;
      toast.success(
        `Geri yüklendi (${cols} koleksiyon${upl ? ' + ' + upl + ' dosya' : ''}). Otomatik yedek: ${res.pre_restore_snapshot}`,
      );
      setConfirmRestore(null);
      uploadedFileRef.current = null;
      loadSnapshots();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Geri yükleme başarısız');
    } finally { setBusy(false); }
  };

  return (
    <div data-testid="admin-backup">
      <div className="mb-6">
        <div className="text-[11px] tracking-[0.3em] uppercase text-coral-300 font-bold">Sistem</div>
        <h1 className="font-display text-4xl mt-2 leading-none">Yedekleme & Geri Yükleme</h1>
        <p className="mt-2 text-ink-400 text-sm max-w-3xl">
          Tüm site içeriğini (sayfalar, projeler, etkinlikler, bağışçılar, ayarlar, kullanıcılar) <strong>ve tüm yüklenmiş PDF/görselleri</strong> tek bir ZIP dosyasında yedekleyin.
          Bir sorun yaşarsanız buradan eski hâline geri yükleyebilirsiniz.
          <strong className="text-ink-600"> Geri yükleme öncesi otomatik bir güvenlik yedeği daha alınır.</strong>
        </p>
      </div>

      {/* Action grid */}
      <div className="grid md:grid-cols-3 gap-4 mb-8">
        <ActionCard
          icon={Download}
          tone="emerald"
          title="JSON İndir (sadece veri)"
          desc="Sadece veritabanı içeriği — PDF/görseller HARİÇ. Küçük, hızlı."
          actionLabel="JSON İndir"
          onClick={downloadJSON}
          busy={busy}
          testid="backup-download"
        />
        <ActionCard
          icon={Camera}
          tone="ink"
          title="Tam Yedek Al (ZIP)"
          desc="Veri + tüm yüklenmiş PDF/görseller. Sıfırdan restore edilebilen tam yedek."
          actionLabel="Tam Yedek Al"
          onClick={takeSnapshot}
          busy={busy}
          testid="backup-snapshot"
        />
        <ActionCard
          icon={Upload}
          tone="gold"
          title="Yedekten Geri Yükle"
          desc=".zip veya .json yedek dosyasıyla sistemi geri yükleyin."
          actionLabel="Dosya Seç"
          onClick={() => fileRef.current?.click()}
          busy={busy}
          testid="backup-upload"
        />
        <input ref={fileRef} type="file" accept=".zip,application/zip,application/json,.json" onChange={onPickFile} className="hidden" />
      </div>

      {/* Snapshots list */}
      <div className="bg-white rounded-2xl border border-ink-100 overflow-hidden">
        <div className="px-6 py-5 border-b border-ink-100 flex items-center justify-between gap-3">
          <div>
            <div className="text-[10px] tracking-[0.25em] uppercase text-coral-300 font-bold">Sunucu Yedekleri</div>
            <div className="font-display text-2xl mt-0.5 flex items-center gap-2"><Database className="w-5 h-5 text-ink-400" /> {snapshots.length} kayıt</div>
          </div>
          <button onClick={loadSnapshots} className="text-xs text-ink-500 hover:text-ink-700 underline underline-offset-2">Yenile</button>
        </div>

        {loading ? (
          <div className="p-10 text-center text-ink-400 text-sm">Yükleniyor...</div>
        ) : snapshots.length === 0 ? (
          <div className="p-10 text-center text-ink-400 text-sm">
            <FileJson className="w-10 h-10 mx-auto mb-3 opacity-40" />
            Henüz snapshot yok. Yukarıdan <strong>Snapshot Al</strong> ile ilk yedeğinizi oluşturun.
          </div>
        ) : (
          <div className="divide-y divide-ink-100">
            {snapshots.map(s => (
              <div key={s.name} className="px-6 py-4 flex flex-wrap items-center gap-4 hover:bg-cream-100" data-testid={`snapshot-${s.name}`}>
                <div className="w-10 h-10 rounded-xl bg-ink-50 grid place-items-center text-ink-600 shrink-0">
                  {s.name.includes('pre-restore') ? <ShieldCheck className="w-4 h-4" /> : <FileJson className="w-4 h-4" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-mono text-sm text-ink-600 truncate">{s.name}</div>
                  <div className="text-xs text-ink-400 mt-0.5 flex items-center gap-3 flex-wrap">
                    <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" />{formatDate(s.created_at)}</span>
                    <span>{formatBytes(s.size)}</span>
                    {s.kind === 'zip' ? (
                      <span className="px-1.5 py-0.5 rounded bg-ink-600 text-cream text-[10px] font-bold uppercase tracking-widest">Tam Yedek</span>
                    ) : (
                      <span className="px-1.5 py-0.5 rounded bg-cream-200 text-ink-500 text-[10px] font-bold uppercase tracking-widest">JSON</span>
                    )}
                    {s.name.includes('pre-restore') && <span className="px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700 text-[10px] font-bold uppercase tracking-widest">Otomatik</span>}
                  </div>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <a
                    href={api.adminBackupDownloadUrl(s.name) + (localStorage.getItem('yekuv_token') ? `?_t=${localStorage.getItem('yekuv_token')}` : '')}
                    onClick={async (e) => { e.preventDefault(); try { const data = await fetch(api.adminBackupDownloadUrl(s.name), { headers: { Authorization: `Bearer ${localStorage.getItem('yekuv_token')}` } }).then(r => r.blob()); const url = URL.createObjectURL(data); const a = document.createElement('a'); a.href = url; a.download = s.name; a.click(); URL.revokeObjectURL(url); } catch { toast.error('İndirilemedi'); } }}
                    className="h-9 px-3 rounded-lg text-xs font-semibold text-ink-600 hover:bg-cream-200 inline-flex items-center gap-1.5"
                    title="İndir"
                  ><Download className="w-3.5 h-3.5" /> İndir</a>
                  <button onClick={() => restoreFromSnapshot(s.name)} className="h-9 px-3 rounded-lg text-xs font-semibold bg-gold/15 text-ink-700 hover:bg-gold/30 inline-flex items-center gap-1.5" title="Geri yükle" data-testid={`restore-${s.name}`}>
                    <RotateCcw className="w-3.5 h-3.5" /> Geri Yükle
                  </button>
                  <button onClick={() => removeSnapshot(s.name)} className="h-9 w-9 grid place-items-center rounded-lg text-coral-300 hover:bg-coral-100" title="Sil">
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Trash bin section — soft-deleted backups, auto-purged after retention window */}
      <div className="mt-6 bg-white rounded-2xl border border-ink-100 overflow-hidden">
        <button
          type="button"
          onClick={() => setShowTrash(v => !v)}
          className="w-full px-6 py-4 flex items-center justify-between gap-3 hover:bg-cream-100"
          data-testid="toggle-trash"
        >
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-coral-50 grid place-items-center text-coral-400"><Recycle className="w-4 h-4" /></div>
            <div className="text-left">
              <div className="font-display text-lg leading-none">Çöp Kutusu</div>
              <div className="text-[11px] text-ink-400 mt-0.5">
                {trash.length === 0 ? 'Boş — silinen yedekler burada 48 saat saklanır' : `${trash.length} yedek · otomatik silim yaklaşıyor`}
              </div>
            </div>
          </div>
          <span className="text-xs text-ink-400">{showTrash ? 'Gizle' : 'Göster'}</span>
        </button>
        {showTrash && (
          <div className="border-t border-ink-100">
            {trash.length === 0 ? (
              <div className="p-8 text-center text-ink-400 text-sm">Çöp Kutusu boş.</div>
            ) : (
              <>
                <div className="px-6 py-3 bg-coral-50 border-b border-coral-100 flex items-center justify-between gap-3">
                  <div className="text-xs text-coral-400">
                    <AlertTriangle className="inline w-3.5 h-3.5 mr-1 -mt-0.5" />
                    Buradaki yedekler <strong>{trash[0]?.retention_hours || 48} saat</strong> sonra otomatik olarak kalıcı silinir. Fikrini değiştirdiysen "Geri Al" ile listeye taşıyabilirsin.
                  </div>
                  <button onClick={emptyTrashAll} className="text-xs font-semibold text-coral-400 hover:text-coral-500 underline">Tümünü kalıcı sil</button>
                </div>
                <div className="divide-y divide-ink-100">
                  {trash.map(t => (
                    <div key={t.name} className="px-6 py-4 flex flex-wrap items-center gap-4 hover:bg-cream-100" data-testid={`trash-${t.name}`}>
                      <div className="w-10 h-10 rounded-xl bg-coral-50 grid place-items-center text-coral-400 shrink-0">
                        <Recycle className="w-4 h-4" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-mono text-sm text-ink-600 truncate">{t.original_name}</div>
                        <div className="text-xs text-ink-400 mt-0.5 flex items-center gap-3 flex-wrap">
                          <span className="inline-flex items-center gap-1"><Trash2 className="w-3 h-3" />{formatDate(t.trashed_at)}</span>
                          <span>{formatBytes(t.size)}</span>
                          {t.kind === 'zip' ? (
                            <span className="px-1.5 py-0.5 rounded bg-ink-600 text-cream text-[10px] font-bold uppercase tracking-widest">Tam Yedek</span>
                          ) : (
                            <span className="px-1.5 py-0.5 rounded bg-cream-200 text-ink-500 text-[10px] font-bold uppercase tracking-widest">JSON</span>
                          )}
                          <span className="inline-flex items-center gap-1 text-coral-400 font-semibold">
                            <Clock className="w-3 h-3" /> {formatUntil(t.expires_at)} kaldı
                          </span>
                        </div>
                      </div>
                      <div className="flex items-center gap-1 shrink-0">
                        <button onClick={() => restoreFromTrash(t.name, t.original_name)} className="h-9 px-3 rounded-lg text-xs font-semibold bg-emerald-100 text-emerald-700 hover:bg-emerald-200 inline-flex items-center gap-1.5" data-testid={`untrash-${t.name}`}>
                          <ArchiveRestore className="w-3.5 h-3.5" /> Geri Al
                        </button>
                        <button onClick={() => purgeTrashOne(t.name, t.original_name)} className="h-9 w-9 grid place-items-center rounded-lg text-coral-300 hover:bg-coral-100" title="Kalıcı sil">
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        )}
      </div>
      {/* Security info note */}
      <div className="mt-6 bg-emerald-50/60 border border-emerald-200 rounded-2xl p-5 text-sm text-ink-600 flex items-start gap-3">
        <ShieldCheck className="w-5 h-5 text-emerald-600 shrink-0 mt-0.5" />
        <div>
          <strong>Güvenlik:</strong> Geri yükleme yapıldığında, mevcut veritabanınızın tam bir snapshot'ı otomatik olarak alınır
          (ad: <code className="text-xs">yekuv-snapshot-pre-restore-…</code>) — böylece yanlış bir geri yükleme yaparsanız bir tıkla eski hâline dönebilirsiniz.
          Yönetici hesapları varsayılan olarak değiştirilmez (admin kilidini önlemek için).
        </div>
      </div>

      {/* Restore confirm modal */}
      {confirmRestore && (
        <div className="fixed inset-0 z-[100] bg-ink-900/70 backdrop-blur-sm grid place-items-center p-4" onClick={() => !busy && setConfirmRestore(null)}>
          <div className="bg-cream rounded-3xl max-w-lg w-full p-7 shadow-2xl" onClick={e => e.stopPropagation()}>
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-xl bg-coral-100 text-coral-300 grid place-items-center shrink-0"><AlertTriangle className="w-6 h-6" /></div>
              <div className="flex-1 min-w-0">
                <div className="text-[10px] tracking-[0.25em] uppercase text-coral-300 font-bold">Geri Yükleme Onayı</div>
                <h3 className="font-display text-2xl mt-1">Bu işlem mevcut verileri değiştirir</h3>
                <p className="mt-3 text-sm text-ink-500">
                  Kaynak: <strong className="text-ink-700 font-mono break-all">{confirmRestore.source}</strong>
                  <br />Devam etmeden önce mevcut sisteminizin otomatik bir yedeği alınacak.
                </p>

                <div className="mt-5 space-y-3 text-sm">
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input type="radio" checked={confirmRestore.mode === 'replace'} onChange={() => setConfirmRestore(c => ({ ...c, mode: 'replace' }))} className="mt-1" />
                    <span><strong>Tam Geri Yükleme (Replace)</strong> — Mevcut tablo içerikleri silinir, yedekteki içerikler birebir geri konulur. <span className="text-coral-300">Önerilen</span></span>
                  </label>
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input type="radio" checked={confirmRestore.mode === 'merge'} onChange={() => setConfirmRestore(c => ({ ...c, mode: 'merge' }))} className="mt-1" />
                    <span><strong>Birleştir (Merge)</strong> — Sadece eşleşen kayıtlar güncellenir; mevcut veriler silinmez.</span>
                  </label>
                  <label className="flex items-start gap-3 cursor-pointer pt-2 border-t border-ink-100">
                    <input type="checkbox" checked={confirmRestore.keep_admin_users} onChange={e => setConfirmRestore(c => ({ ...c, keep_admin_users: e.target.checked }))} className="mt-1" />
                    <span><strong>Yönetici hesaplarını koru</strong> — admin_users tablosu değiştirilmez (admin kilidini önler). Önerilen.</span>
                  </label>
                  {(confirmRestore.zip_file || (confirmRestore.snapshot_name || '').endsWith('.zip')) && (
                    <label className="flex items-start gap-3 cursor-pointer">
                      <input type="checkbox" checked={restoreUploads} onChange={e => setRestoreUploads(e.target.checked)} className="mt-1" />
                      <span><strong>Yüklenmiş PDF/görselleri de geri yükle</strong> — ZIP içindeki <code className="text-xs">uploads/</code> klasörü diske geri yazılır. Önerilen.</span>
                    </label>
                  )}
                </div>

                <div className="mt-7 flex justify-end gap-2">
                  <button onClick={() => setConfirmRestore(null)} disabled={busy} className="h-10 px-4 rounded-full text-sm font-semibold text-ink-500 hover:bg-cream-200">Vazgeç</button>
                  <button onClick={doRestore} disabled={busy} className="h-10 px-5 rounded-full text-sm font-semibold bg-coral-300 text-white hover:bg-coral-400 inline-flex items-center gap-2 disabled:opacity-60" data-testid="confirm-restore">
                    <RotateCcw className="w-4 h-4" /> {busy ? 'Geri yükleniyor...' : 'Onayla & Geri Yükle'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function ActionCard({ icon: Icon, tone = 'ink', title, desc, actionLabel, onClick, busy, testid }) {
  const tones = {
    emerald: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    ink: 'bg-ink-50 text-ink-600 border-ink-100',
    gold: 'bg-gold/15 text-ink-700 border-gold/30',
  };
  return (
    <div className="bg-white rounded-2xl border border-ink-100 p-6 flex flex-col">
      <div className={`w-12 h-12 rounded-xl grid place-items-center border ${tones[tone]}`}><Icon className="w-5 h-5" /></div>
      <div className="font-display text-xl mt-4 text-ink-600">{title}</div>
      <p className="text-sm text-ink-400 mt-1.5 flex-1">{desc}</p>
      <button onClick={onClick} disabled={busy} data-testid={testid} className="mt-5 h-11 rounded-full bg-ink-600 text-cream text-sm font-semibold hover:bg-ink-500 disabled:opacity-60">
        {busy ? '...' : actionLabel}
      </button>
    </div>
  );
}
